<template>
  <div class="container p-4 sync-django">
    <div class="flex flex-wrap align-items-center gap-2 mb-3">
      <ButtonPrime icon="pi pi-arrow-left" class="p-button-text" @click="router.push({ name: 'estudyoo-hub' })" />
      <h3 class="text-xl font-bold m-0">Sincronización → Django</h3>
      <ButtonPrime
        label="Reporte por colegio"
        icon="pi pi-table"
        class="p-button-sm p-button-outlined ml-auto"
        @click="router.push({ name: 'estudyoo-reporte-sync' })"
      />
    </div>
    <p class="text-sm text-600 mb-4">
      <strong>Orden:</strong> colegio → catálogo completo (materias + ejes del paquete) →
      <em>por cada curso</em> → capítulos → alumnos.
      Por defecto los registros iguales se <strong>omiten</strong>; activa <strong>Forzar actualización</strong> para re-registrar todo.
    </p>

    <CardPrime class="mb-4">
      <template #title>0. Sesión admin legacy (Google → admin1)</template>
      <template #content>
        <p class="text-sm text-600 mb-3">
          Colegios y SyncDjango vienen del API C# en
          <code>admin1.estudyoo.cl</code>. Requiere iniciar sesión con Google en este sitio
          (<code>usuarios1.estudyoo.cl</code>), independiente del login Django.
        </p>
        <div class="flex flex-wrap align-items-center gap-2">
          <TagPrime v-if="legacyOk" severity="success" value="Token legacy activo" />
          <TagPrime v-else severity="danger" value="Sin sesión Google / token expirado" />
          <ButtonPrime
            v-if="!legacyOk"
            label="Iniciar sesión con Google"
            icon="pi pi-google"
            class="p-button-outlined"
            @click="router.push({ name: 'login', query: { redirect: 'sync-django' } })"
          />
          <ButtonPrime
            label="Recargar colegios"
            icon="pi pi-refresh"
            class="p-button-sm p-button-text"
            :disabled="!legacyOk"
            :loading="cargandoColegios"
            @click="cargarColegios"
          />
        </div>
      </template>
    </CardPrime>

    <CardPrime class="mb-4">
      <template #title>1. Autenticación Django</template>
      <template #content>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label class="block text-sm mb-1">Usuario staff Django</label>
            <InputText v-model="djangoUser" class="w-full" placeholder="estudyoo_admin_demo" />
          </div>
          <div>
            <label class="block text-sm mb-1">Contraseña</label>
            <InputText v-model="djangoPass" type="password" class="w-full" />
          </div>
          <div class="flex align-items-end gap-2 flex-wrap">
            <ButtonPrime label="Conectar" icon="pi pi-lock" :loading="conectando" @click="conectarDjango" />
            <TagPrime v-if="djangoOk" severity="success" value="Conectado" />
            <TagPrime v-else severity="warning" value="Sin token" />
          </div>
        </div>
        <p class="text-xs text-500 mt-2">API: {{ backUrl || '—' }}</p>
      </template>
    </CardPrime>

    <CardPrime class="mb-4">
      <template #title>2. Colegio y alcance</template>
      <template #content>
        <div class="mb-3">
          <label class="block text-sm mb-1">Colegio (legacy)</label>
          <DropdownPrime
            v-model="selColegioId"
            :options="colegios"
            option-label="Glosa"
            option-value="Id"
            :placeholder="cargandoColegios ? 'Cargando colegios…' : colegiosError ? 'Error al cargar' : 'Selecciona colegio'"
            class="w-full"
            show-clear
            :disabled="cargandoColegios"
            :loading="cargandoColegios"
            @change="onColegioChange"
          />
          <p class="text-xs text-500 mt-2">
            Listado desde el admin legacy (C#): <code>{{ legacyApiUrl }}</code>
            · {{ colegios.length }} colegio(s)
          </p>
          <p v-if="colegiosError" class="text-sm text-red-500 mt-2">{{ colegiosError }}</p>
          <ButtonPrime
            v-if="colegiosError || !colegios.length"
            label="Reintentar carga de colegios"
            icon="pi pi-refresh"
            class="p-button-sm p-button-outlined mt-2"
            :loading="cargandoColegios"
            @click="cargarColegios"
          />
        </div>
        <div class="mb-3">
          <label class="block text-sm mb-1">Alcance del sync</label>
          <DropdownPrime
            v-model="selCursoId"
            :options="opcionesAlcance"
            option-label="label"
            option-value="value"
            placeholder="Todo el colegio"
            class="w-full"
            show-clear
            :disabled="!selColegioId || cargandoCursos"
            :loading="cargandoCursos"
            @change="onAlcanceChange"
          />
          <p class="text-xs text-500 mt-2">
            <strong>Todo el colegio:</strong> catálogo completo (todos los cursos).
            <strong>Un curso:</strong> rama curso → materia → eje → capítulo + alumnos de ese curso.
          </p>
          <div class="flex flex-wrap gap-2 mt-2">
            <ButtonPrime
              label="Preset: catálogo + alumnos"
              icon="pi pi-sitemap"
              class="p-button-sm p-button-outlined"
              :disabled="sincronizando"
              @click="presetCatalogoAlumnos"
            />
            <ButtonPrime
              label="Preset: reparar catálogo"
              icon="pi pi-wrench"
              class="p-button-sm p-button-outlined p-button-warning"
              :disabled="sincronizando"
              @click="presetRepararCatalogo"
            />
          </div>
          <div class="flex align-items-center gap-2 mt-3">
            <CheckboxPrime v-model="forzarActualizacion" input-id="sync-forzar" :binary="true" :disabled="sincronizando" />
            <label for="sync-forzar" class="text-sm cursor-pointer">
              Forzar actualización (no omitir registros ya iguales; re-registra espejo sync)
            </label>
          </div>
          <div class="mt-3">
            <label class="block text-sm mb-1">Registro de eventos en Django</label>
            <DropdownPrime
              v-model="modoEventosSync"
              :options="opcionesModoEventos"
              option-label="label"
              option-value="value"
              class="w-full"
              :disabled="sincronizando"
              @change="onModoEventosChange"
            />
            <p class="text-xs text-500 mt-2 mb-0">
              Cada evento hace POST a <code>/estudyoo-sync/sesion/…/evento/</code>.
              Usa <strong>Desactivado</strong> + lote ×50–90 para evitar timeouts en DigitalOcean.
            </p>
          </div>
          <div class="mt-3">
            <label class="block text-sm mb-1">Tamaño de lote (registros por request bulk)</label>
            <DropdownPrime
              v-model="batchSizeSync"
              :options="opcionesTamanoLote"
              option-label="label"
              option-value="value"
              class="w-full md:w-20rem"
              :disabled="sincronizando"
            />
            <p class="text-xs text-500 mt-2 mb-0">
              Un solo POST por lote con hasta {{ batchSizeSync }} registros en
              <code>items[]</code>. El backend procesa el lote completo, responde, y recién ahí se envía el siguiente (sin solapamiento).
            </p>
          </div>
        </div>
        <div class="flex flex-wrap gap-3 mb-3">
          <div v-for="f in faseOpts" :key="f.key" class="flex align-items-center gap-1">
            <CheckBox v-model="fases[f.key]" :input-id="'f-' + f.key" :binary="true" />
            <label :for="'f-' + f.key" class="text-sm ml-1">{{ f.label }}</label>
          </div>
        </div>
        <div class="flex flex-wrap gap-2 mb-3">
          <ButtonPrime
            label="Previsualizar paquete legacy"
            icon="pi pi-eye"
            class="p-button-outlined"
            :disabled="!selColegioId || cargandoPaquete || sincronizando"
            :loading="cargandoPaquete"
            @click="previsualizarPaquete"
          />
          <ButtonPrime
            :label="`Iniciar sync (lotes ×${batchSizeSync})`"
            icon="pi pi-sync"
            class="p-button-success"
            :disabled="!puedeSync"
            :loading="sincronizando && !deteniendoSync"
            @click="iniciarSync"
          />
          <ButtonPrime
            v-if="sincronizando"
            label="Detener sync"
            icon="pi pi-stop-circle"
            class="p-button-danger"
            :loading="deteniendoSync"
            @click="detenerSync"
          />
          <ButtonPrime label="Refrescar resumen" icon="pi pi-refresh" :disabled="!sesionId || refrescandoResumen" :loading="refrescandoResumen" @click="refrescarResumen" />
        </div>
        <div v-if="paqueteResumen" class="sync-preview p-3 mb-3 border-round surface-100">
          <p class="text-sm font-semibold m-0 mb-2">Paquete legacy listo</p>
          <div class="flex flex-wrap gap-3 text-sm">
            <span><strong>Colegio:</strong> {{ paqueteResumen.colegio }}</span>
            <span v-if="paqueteResumen.alcance === 'curso'">
              <strong>Curso:</strong> {{ labelCursoSeleccionado }}
            </span>
            <span v-else><strong>Alcance:</strong> colegio completo</span>
            <span><strong>Comuna:</strong> {{ paqueteResumen.comunaLegacy || '—' }}</span>
            <span><strong>Región:</strong> {{ paqueteResumen.regionLegacy || '—' }}</span>
            <span>{{ paqueteResumen.cursos }} cursos</span>
            <span>{{ paqueteResumen.asignaturas }} asignaturas</span>
            <span>{{ paqueteResumen.ejes }} ejes</span>
            <span>{{ paqueteResumen.capitulos }} capítulos</span>
          </div>
          <details v-if="colegioPayloadPreview" class="mt-2 text-xs">
            <summary class="cursor-pointer text-primary">Payload colegio → Django (sin objetos anidados)</summary>
            <pre class="mt-1 p-2 surface-0 border-round overflow-auto">{{ JSON.stringify(colegioPayloadPreview, null, 2) }}</pre>
          </details>
        </div>
        <div v-if="sincronizando || progreso.paso > 0" class="mb-3">
          <div class="flex justify-content-between text-sm mb-1">
            <span>{{ progreso.label || (deteniendoSync ? 'Deteniendo…' : 'Sincronizando…') }}</span>
            <span class="text-500">Paso {{ progreso.paso }}</span>
          </div>
          <ProgressBar mode="indeterminate" style="height: 6px" />
          <p v-if="deteniendoSync" class="text-xs text-orange-600 mt-1 mb-0">
            Esperando que termine el lote o request en curso…
          </p>
          <p v-if="progreso.mensaje" class="text-xs text-500 mt-1 mb-0">{{ progreso.mensaje }}</p>
          <p v-if="progreso.batchSize" class="text-xs text-500 mt-1 mb-0">
            Lotes secuenciales: {{ progreso.batchSize || batchSizeSync }} registros por payload
          </p>
        </div>
      </template>
    </CardPrime>

    <CardPrime v-if="sesionId" class="mb-4">
      <template #title>3. Sesión {{ sesionId }}</template>
      <template #content>
        <p class="text-sm mb-2">
          Estado: <strong>{{ resumenSesion.estado || '—' }}</strong>
          · OK: {{ resumenSesion.totales?.ok ?? 0 }}
          · Omitidos: {{ resumenSesion.totales?.omitido ?? 0 }}
          · Errores: <strong :class="(resumenSesion.totales?.error ?? 0) > 0 ? 'text-red-600' : ''">{{ resumenSesion.totales?.error ?? 0 }}</strong>
        </p>
        <p class="text-xs text-500 mb-2">
          El log completo queda guardado en Django (espejo). Puedes recuperarlo después con el ID de sesión.
        </p>
        <div class="flex flex-wrap gap-2 mb-3">
          <ButtonPrime
            label="Cargar log Django"
            icon="pi pi-database"
            class="p-button-sm p-button-outlined"
            :loading="cargandoLogServidor"
            @click="cargarLogServidor(false)"
          />
          <ButtonPrime
            label="Solo errores"
            icon="pi pi-exclamation-triangle"
            class="p-button-sm p-button-outlined p-button-danger"
            :loading="cargandoLogServidor"
            @click="cargarLogServidor(true)"
          />
          <ButtonPrime
            label="Descargar errores JSON"
            icon="pi pi-download"
            class="p-button-sm p-button-text"
            :disabled="!logErrores.length"
            @click="descargarErrores"
          />
        </div>
        <DataTable v-if="tablaEntidades.length" :value="tablaEntidades" class="p-datatable-sm mb-3">
          <ColumnPrime field="entidad" header="Entidad" />
          <ColumnPrime field="total" header="Total" />
          <ColumnPrime field="ok" header="OK" />
          <ColumnPrime field="omitido" header="Omitidos" />
          <ColumnPrime field="error" header="Errores" />
        </DataTable>
        <div v-if="sesionesAnteriores.length" class="mb-2">
          <label class="block text-sm mb-1">Sesiones anteriores (mismo colegio)</label>
          <div class="flex flex-wrap gap-2">
            <ButtonPrime
              v-for="s in sesionesAnteriores"
              :key="s.sesion_id"
              :label="`${s.nombre || s.sesion_id.slice(0, 8)} · ${s.totales?.error || 0} err`"
              class="p-button-sm p-button-text"
              @click="abrirSesionAnterior(s.sesion_id)"
            />
          </div>
        </div>
      </template>
    </CardPrime>

    <CardPrime>
      <template #title>4. Log {{ logModoServidor ? '(Django — persistente)' : '(en vivo)' }}</template>
      <template #content>
        <div class="flex flex-wrap align-items-center gap-2 mb-2">
          <TagPrime v-if="logModoServidor" severity="info" :value="`${logMostrado.length} / ${logTotalServidor} registros`" />
          <TagPrime v-else severity="secondary" value="Vista en memoria (máx. 2000)" />
          <span v-if="logErrores.length" class="text-sm text-red-600 font-semibold">
            {{ logErrores.length }} error(es) cargado(s)
          </span>
        </div>
        <DataTable
          :value="logMostrado"
          class="p-datatable-sm sync-log-table"
          scrollable
          scroll-height="480px"
          :empty-message="sincronizando ? 'Sincronizando…' : 'Sin eventos aún'"
          v-model:expandedRows="expandedRows"
          data-key="rowKey"
        >
          <ColumnPrime expander style="width: 2.5rem" />
          <ColumnPrime field="entidad" header="Entidad" style="min-width: 6rem" />
          <ColumnPrime header="Acción" style="min-width: 6rem">
            <template #body="{ data }">
              <TagPrime :severity="accionSeverity(data.accion)" :value="labelAccion(data.accion)" />
            </template>
          </ColumnPrime>
          <ColumnPrime field="fase" header="Fase" style="min-width: 5rem" />
          <ColumnPrime field="id" header="ID legacy" style="min-width: 5rem" />
          <ColumnPrime field="etiqueta" header="Objeto" style="min-width: 8rem" />
          <ColumnPrime field="mensaje" header="Resumen" style="min-width: 12rem" />
          <ColumnPrime field="djangoId" header="UUID Django" style="min-width: 8rem">
            <template #body="{ data }">
              <span class="text-xs font-mono">{{ data.djangoId ? data.djangoId.slice(0, 8) + '…' : '—' }}</span>
            </template>
          </ColumnPrime>
          <template #expansion="{ data }">
            <div v-if="data.cambios?.length" class="p-2 pl-4">
              <p class="text-sm font-semibold mb-1">Campos</p>
              <ul class="text-sm m-0 pl-3">
                <li v-for="(c, i) in data.cambios" :key="i">
                  <template v-if="c.campo === 'stale'">
                    ID <strong>{{ c.anterior }}</strong> ya no viene del legacy
                  </template>
                  <template v-else-if="c.anterior == null">
                    <strong>{{ c.campo }}</strong> = {{ c.nuevo }}
                  </template>
                  <template v-else>
                    <strong>{{ c.campo }}</strong>: {{ c.anterior }} → {{ c.nuevo }}
                  </template>
                </li>
              </ul>
            </div>
            <p v-else class="text-sm text-500 p-2 pl-4">Sin desglose de campos.</p>
          </template>
        </DataTable>
      </template>
    </CardPrime>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import ButtonPrime from 'primevue/button';
import CheckboxPrime from 'primevue/checkbox';
import ProgressBar from 'primevue/progressbar';
import ColegioService from '@/services/ColegioService';
import CursoService from '@/services/CursoService';
import * as DjangoSync from '@/services/DjangoSyncService';
import {
  ejecutarSincronizacionCompleta,
  buildSyncLogEntry,
  cargarPaqueteLegacy,
  resumirPaquete,
  payloadColegioDjango,
  BATCH_SIZE_SYNC_OPTS,
  BATCH_SIZE_SYNC_DEFAULT,
  EVENTOS_SYNC_MODOS,
  leerModoEventosSync,
  guardarModoEventosSync,
  SyncAbortError,
} from '@/services/syncRunner';
import {
  cargarLogSesionCompleto,
  descargarErroresSesion,
  guardarSesionLocal,
  leerSesionLocal,
} from '@/services/syncLogHelpers';
import { hasLegacyAccessToken } from '@/services/legacyAuth';
import store from '@/store/index';

const router = useRouter();
const toast = useToast();

const backUrl = process.env.VUE_APP_BACK_URL || '';
const djangoUser = ref('');
const djangoPass = ref('');
const djangoOk = ref(!!DjangoSync.getDjangoSyncToken());
const legacyOk = ref(hasLegacyAccessToken(store));
const conectando = ref(false);

const colegios = ref([]);
const selColegioId = ref(null);
const colegioNombre = ref('');
const cursos = ref([]);
const selCursoId = ref(null);
const cargandoCursos = ref(false);
const cargandoColegios = ref(false);
const colegiosError = ref('');

const legacyApiUrl = `${(process.env.VUE_APP_API_URL || '').replace(/\/$/, '')}/Colegio/v1/list`;

const fases = ref({
  colegio: true,
  cursos: true,
  asignaturas: true,
  ejes: true,
  capitulos: true,
  preguntas: false,
  actividades: false,
  alumnos: true,
  contratos: false,
});
const forzarActualizacion = ref(false);
const modoEventosSync = ref(leerModoEventosSync());
const opcionesModoEventos = EVENTOS_SYNC_MODOS;
const batchSizeSync = ref(BATCH_SIZE_SYNC_DEFAULT);
const opcionesTamanoLote = ref(BATCH_SIZE_SYNC_OPTS.map((n) => ({
  label: `${n} registros por lote`,
  value: n,
})));

const faseOpts = [
  { key: 'colegio', label: '1. Colegio + periodo' },
  { key: 'cursos', label: '2. Cursos (inicio de cada rama)' },
  { key: 'asignaturas', label: '3. Materias / asignaturas' },
  { key: 'ejes', label: '4. Ejes temáticos' },
  { key: 'capitulos', label: '5. Capítulos' },
  { key: 'preguntas', label: '6. Preguntas juego (por capítulo)' },
  { key: 'actividades', label: '7. Actividades (aviso — solo Django)' },
  { key: 'alumnos', label: '8. Alumnos (tras catálogo del curso)' },
  { key: 'contratos', label: '9. Contratos (con alumnos)' },
];

const sesionId = ref(null);
const resumenSesion = ref({});
const sincronizando = ref(false);
const deteniendoSync = ref(false);
const syncAbortController = ref(null);
const cargandoPaquete = ref(false);
const paqueteCache = ref(null);
const paqueteResumen = ref(null);
const colegioPayloadPreview = ref(null);
const progreso = ref({ paso: 0, label: '', mensaje: '', fase: '' });
const logVivo = ref([]);
const logServidor = ref([]);
const logErrores = ref([]);
const logModoServidor = ref(false);
const logTotalServidor = ref(0);
const cargandoLogServidor = ref(false);
const refrescandoResumen = ref(false);
const sesionesAnteriores = ref([]);
const expandedRows = ref([]);
let logSeq = 0;

const logMostrado = computed(() => (logModoServidor.value ? logServidor.value : logVivo.value));

function accionSeverity(accion) {
  const map = {
    creado: 'success',
    actualizado: 'warning',
    omitido: 'info',
    reconciliar: 'secondary',
    info: 'info',
    error: 'danger',
  };
  return map[accion] || 'secondary';
}

function labelAccion(accion) {
  const map = {
    creado: 'Creado',
    actualizado: 'Actualizado',
    omitido: 'Omitido',
    reconciliar: 'Reconciliar',
    info: 'Info',
    error: 'Error',
  };
  return map[accion] || accion || '—';
}

const puedeSync = computed(
  () => djangoOk.value && selColegioId.value != null && !sincronizando.value,
);

const opcionesAlcance = computed(() =>
  cursos.value.map((c) => ({
    value: c.Id,
    label: c.Glosa || `Curso #${c.Id}`,
  })),
);

const labelCursoSeleccionado = computed(() => {
  if (!selCursoId.value) return '—';
  const c = cursos.value.find((x) => x.Id === selCursoId.value);
  return c?.Glosa || `Curso #${selCursoId.value}`;
});

const tablaEntidades = computed(() => {
  const pe = resumenSesion.value.por_entidad || {};
  return Object.keys(pe).map((k) => ({
    entidad: k,
    ...pe[k],
  }));
});

onMounted(() => {
  legacyOk.value = hasLegacyAccessToken(store);
  if (legacyOk.value) cargarColegios();
  if (djangoOk.value) cargarOpcionesSyncDjango();
  const ultima = leerSesionLocal();
  if (ultima) {
    sesionId.value = ultima;
    refrescarResumen().catch(() => {});
  }
});

async function cargarOpcionesSyncDjango() {
  try {
    const { data } = await DjangoSync.getCatalogo();
    if (Array.isArray(data.batch_size_options) && data.batch_size_options.length) {
      opcionesTamanoLote.value.splice(
        0,
        opcionesTamanoLote.value.length,
        ...data.batch_size_options.map((n) => ({
          label: `${n} registros por lote`,
          value: n,
        })),
      );
    }
    if (data.batch_size_default) {
      batchSizeSync.value = data.batch_size_default;
    }
  } catch {
    /* usar defaults locales */
  }
}

async function cargarColegios() {
  cargandoColegios.value = true;
  colegiosError.value = '';
  try {
    const r = await ColegioService.get();
    const lista = Array.isArray(r.data) ? r.data : [];
    colegios.value = lista.filter((c) => c && c.Id != null && c.Id !== 0);
    legacyOk.value = true;
    if (!colegios.value.length) {
      colegiosError.value = 'No se recibieron colegios. ¿Iniciaste sesión con Google en el admin?';
      toast.add({
        severity: 'warn',
        summary: 'Sin colegios',
        detail: 'El listado legacy llegó vacío o solo con "NO APLICA".',
        life: 6000,
      });
    }
  } catch (e) {
    colegios.value = [];
    const st = e.response?.status;
    const det = e.response?.data;
    const msg = typeof det === 'string' ? det : det?.detail || det?.message || e.message || 'Error de red';
    if (st === 401) {
      legacyOk.value = false;
      colegiosError.value =
        'Sesión legacy expirada o inválida (401). Cierra sesión e inicia de nuevo con Google en usuarios1.estudyoo.cl.';
    } else {
      colegiosError.value = `No se pudieron cargar colegios: ${msg}`;
    }
    toast.add({
      severity: 'error',
      summary: 'Colegios legacy',
      detail: colegiosError.value,
      life: 8000,
    });
  } finally {
    cargandoColegios.value = false;
  }
}

function onColegioChange() {
  const c = colegios.value.find((x) => x.Id === selColegioId.value);
  colegioNombre.value = c?.Glosa || '';
  selCursoId.value = null;
  paqueteCache.value = null;
  paqueteResumen.value = null;
  colegioPayloadPreview.value = null;
  if (selColegioId.value) cargarCursos();
  else cursos.value = [];
}

function onAlcanceChange() {
  paqueteCache.value = null;
  paqueteResumen.value = null;
  colegioPayloadPreview.value = null;
}

function presetCatalogoAlumnos() {
  fases.value = {
    colegio: true,
    cursos: true,
    asignaturas: true,
    ejes: true,
    capitulos: true,
    preguntas: false,
    actividades: false,
    alumnos: true,
    contratos: false,
  };
  forzarActualizacion.value = false;
}

function presetRepararCatalogo() {
  fases.value = {
    colegio: true,
    cursos: false,
    asignaturas: true,
    ejes: true,
    capitulos: true,
    preguntas: false,
    actividades: false,
    alumnos: false,
    contratos: false,
  };
  selCursoId.value = null;
  forzarActualizacion.value = true;
}

async function cargarCursos() {
  cargandoCursos.value = true;
  try {
    const { data } = await CursoService.get();
    cursos.value = (Array.isArray(data) ? data : []).filter((c) => c && c.Id != null && c.Vigente !== false);
  } catch {
    cursos.value = [];
  } finally {
    cargandoCursos.value = false;
  }
}

function pushLog(entry) {
  entry.rowKey = `${++logSeq}-${entry.entidad}-${entry.id}-${entry.accion}`;
  logVivo.value.unshift(entry);
  if (logVivo.value.length > 2000) logVivo.value.pop();
}

async function cargarSesionesAnteriores() {
  if (!selColegioId.value || !djangoOk.value) return;
  try {
    const { data } = await DjangoSync.listarSesiones({
      colegio_id_externo: String(selColegioId.value),
      limit: 8,
    });
    sesionesAnteriores.value = (data.sesiones || []).filter((s) => s.sesion_id !== sesionId.value);
  } catch {
    sesionesAnteriores.value = [];
  }
}

async function cargarLogServidor(soloErrores = false) {
  if (!sesionId.value) return;
  cargandoLogServidor.value = true;
  try {
    const { logs, total } = await cargarLogSesionCompleto(sesionId.value, {
      soloError: soloErrores,
      orden: soloErrores ? 'asc' : 'desc',
    });
    logServidor.value = logs;
    logTotalServidor.value = total;
    logModoServidor.value = true;
    if (soloErrores) {
      logErrores.value = logs;
      toast.add({
        severity: logs.length ? 'warn' : 'success',
        summary: 'Errores de sesión',
        detail: logs.length ? `${logs.length} error(es) en Django` : 'Sin errores en esta sesión',
        life: 5000,
      });
    }
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Log Django',
      detail: e.response?.data?.detail || e.message,
      life: 6000,
    });
  } finally {
    cargandoLogServidor.value = false;
  }
}

function descargarErrores() {
  if (!sesionId.value || !logErrores.value.length) return;
  descargarErroresSesion(sesionId.value, logErrores.value, {
    colegio: colegioNombre.value,
    resumen: resumenSesion.value,
  });
}

async function abrirSesionAnterior(id) {
  sesionId.value = id;
  guardarSesionLocal(id);
  logVivo.value = [];
  logModoServidor.value = false;
  await refrescarResumen();
  await cargarLogServidor(true);
}

async function previsualizarPaquete() {
  if (!selColegioId.value) return;
  cargandoPaquete.value = true;
  try {
    paqueteCache.value = await cargarPaqueteLegacy(selColegioId.value, { idCurso: selCursoId.value || undefined });
    paqueteResumen.value = resumirPaquete(paqueteCache.value);
    colegioPayloadPreview.value = payloadColegioDjango(paqueteCache.value?.colegio);
    const alcance = paqueteResumen.value.alcance === 'curso' ? labelCursoSeleccionado.value : 'colegio completo';
    toast.add({
      severity: 'success',
      summary: 'Paquete legacy',
      detail: `${alcance} · ${paqueteResumen.value.capitulos} capítulos · comuna: ${paqueteResumen.value.comunaLegacy || '—'}`,
      life: 5000,
    });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error paquete legacy',
      detail: e.response?.data?.message || e.message,
      life: 6000,
    });
  } finally {
    cargandoPaquete.value = false;
  }
}

async function conectarDjango() {
  conectando.value = true;
  try {
    await DjangoSync.loginDjango(djangoUser.value, djangoPass.value);
    djangoOk.value = true;
    await cargarOpcionesSyncDjango();
    toast.add({ severity: 'success', summary: 'Django', detail: 'Token obtenido', life: 3000 });
  } catch (e) {
    djangoOk.value = false;
    toast.add({
      severity: 'error',
      summary: 'Error login Django',
      detail: e.response?.data?.detail || e.message,
      life: 5000,
    });
  } finally {
    conectando.value = false;
  }
}

async function refrescarResumen() {
  if (!sesionId.value) return;
  refrescandoResumen.value = true;
  try {
    const { data } = await DjangoSync.detalleSesion(sesionId.value);
    resumenSesion.value = data;
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Resumen',
      detail: e.response?.data?.detail || e.message,
      life: 5000,
    });
  } finally {
    refrescandoResumen.value = false;
  }
}

function paqueteCacheValido() {
  if (!paqueteCache.value) return false;
  const cursoCache = paqueteCache.value.meta?.id_curso ?? null;
  const cursoSel = selCursoId.value ?? null;
  return String(cursoCache ?? '') === String(cursoSel ?? '');
}

function onModoEventosChange() {
  guardarModoEventosSync(modoEventosSync.value);
}

async function iniciarSync() {
  if (!puedeSync.value) return;
  sincronizando.value = true;
  deteniendoSync.value = false;
  syncAbortController.value = new AbortController();
  logVivo.value = [];
  expandedRows.value = [];
  logSeq = 0;
  progreso.value = { paso: 0, label: 'Iniciando…', mensaje: '', fase: '' };
  try {
    const nombreCurso = selCursoId.value ? labelCursoSeleccionado.value : null;
    const { data: ses } = await DjangoSync.iniciarSesion({
      nombre: nombreCurso ? `Sync ${colegioNombre.value} · ${nombreCurso}` : `Sync ${colegioNombre.value}`,
      colegio_id_externo: String(selColegioId.value),
      colegio_nombre: colegioNombre.value,
    });
    sesionId.value = ses.sesion_id;
    guardarSesionLocal(ses.sesion_id);
    logModoServidor.value = false;
    logServidor.value = [];
    logErrores.value = [];

    const result = await ejecutarSincronizacionCompleta({
      colegioId: selColegioId.value,
      cursoId: selCursoId.value || undefined,
      colegioNombre: colegioNombre.value,
      sesionId: sesionId.value,
      fases: fases.value,
      forzarActualizacion: forzarActualizacion.value,
      modoEventosSync: modoEventosSync.value,
      batchSize: batchSizeSync.value,
      signal: syncAbortController.value.signal,
      paquete: paqueteCacheValido() ? paqueteCache.value : undefined,
      onStatus: (st) => {
        progreso.value = { ...st };
      },
      onProgress: ({ fase, item, result, error, tipo }) => {
        if (tipo === 'info') {
          pushLog(buildSyncLogEntry({ fase, item, result, tipo: 'info' }));
          return;
        }
        pushLog(buildSyncLogEntry({ fase, item, result, error }));
      },
    });

    if (result.paquete) {
      paqueteCache.value = result.paquete;
      paqueteResumen.value = resumirPaquete(result.paquete);
      colegioPayloadPreview.value = payloadColegioDjango(result.paquete?.colegio);
    }

    await refrescarResumen();
    await cargarSesionesAnteriores();
    await cargarLogServidor(true);
    const nErr = result.finalizacion?.errores_total ?? resumenSesion.value.totales?.error ?? 0;
    if (result.abortada) {
      toast.add({
        severity: 'warn',
        summary: 'Sync detenida',
        detail: `${result.pasosEjecutados} pasos ejecutados · progreso guardado en Django`,
        life: 6000,
      });
    } else {
      toast.add({
        severity: nErr > 0 ? 'warn' : 'success',
        summary: 'Sync finalizada',
        detail: nErr > 0
          ? `${result.pasosEjecutados} pasos · ${nErr} error(es) — revisa log Django`
          : `${result.pasosEjecutados} pasos · sin errores`,
        life: 6000,
      });
    }
  } catch (e) {
    if (e instanceof SyncAbortError) {
      toast.add({
        severity: 'warn',
        summary: 'Sync detenida',
        detail: 'La sincronización fue cancelada.',
        life: 5000,
      });
    } else if (sesionId.value && modoEventosSync.value !== 'off') {
      const registrar = modoEventosSync.value === 'completo' || modoEventosSync.value === 'solo_errores';
      if (registrar) {
      try {
        await DjangoSync.registrarEvento(sesionId.value, {
          evento: {
            entidad: 'colegio',
            id_externo: '—',
            accion: 'error',
            ok: false,
            mensaje: e.message || 'Sync abortada',
            fase: 'sync',
            fuente: 'cliente',
          },
        });
        await cargarLogServidor(true);
      } catch {
        /* ignore */
      }
      }
    }
    if (!(e instanceof SyncAbortError)) {
      toast.add({
        severity: 'error',
        summary: 'Sync fallida',
        detail: e.message,
        life: 6000,
      });
    }
  } finally {
    sincronizando.value = false;
    deteniendoSync.value = false;
    syncAbortController.value = null;
    progreso.value = { paso: 0, label: '', mensaje: '', fase: '' };
  }
}

function detenerSync() {
  if (!syncAbortController.value || deteniendoSync.value) return;
  deteniendoSync.value = true;
  syncAbortController.value.abort();
  progreso.value = {
    ...progreso.value,
    label: 'Deteniendo sync…',
    mensaje: 'Esperando que termine el lote en curso',
  };
}
</script>

<style scoped>
.sync-django :deep(.p-datatable) {
  font-size: 0.85rem;
}
.sync-log-table :deep(.font-mono) {
  font-family: ui-monospace, monospace;
}
</style>
